/**
 * Companies -- see them, add one.
 *
 * Creating a company used to need someone on the server. The API could do
 * it all along; there was simply no screen, so a second company was a
 * support request rather than something the owner could do.
 *
 * The form asks for as little as will produce a company that can post on
 * day one: a name and the date its year starts. The chart of accounts is
 * provisioned automatically, the code is generated, the year runs twelve
 * months. VAT and CR are offered because they print on every invoice, but
 * they can be filled in later on the Company tab once it is open.
 *
 * Who else may open the new company is NOT asked here. It is decided on
 * each user, on the Users tab -- one place for that question, not two.
 */
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, Skeleton, Stack, TextField, Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { useTranslation } from 'react-i18next'

import { createCompany, fetchCompanies } from '../api/endpoints'
import { errorMessage } from '../api/client'
import type { CompanyRow } from '../api/types'
import { useAuth } from '../store/AuthContext'
import { c } from '../theme'
import { gregorian } from '../reports/hijri'

/** 1 January of this year -- the start most Gulf companies use. */
function thisYearStart(): string {
  return `${new Date().getFullYear()}-01-01`
}

/** The day before the same date next year, matching the server. */
export function yearEnd(start: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(start)
  if (!m) return null
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  // Built from calendar fields, never toISOString(): that is UTC, and
  // between midnight and 3am in Riyadh it answers with yesterday.
  const next = new Date(y + 1, mo - 1, Math.min(d, mo === 2 && d === 29 ? 28 : d))
  next.setDate(next.getDate() - 1)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${next.getFullYear()}-${p(next.getMonth() + 1)}-${p(next.getDate())}`
}

export default function CompaniesPanel() {
  const { t, i18n } = useTranslation()
  const [open, setOpen] = useState(false)
  const [created, setCreated] = useState<CompanyRow | null>(null)
  const q = useQuery({ queryKey: ['companies'], queryFn: fetchCompanies })

  return (
    <>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="body2" sx={{ color: c.inkMuted, flex: 1 }}>
          {t('companies.intro')}
        </Typography>
        <Button variant="contained" size="small" startIcon={<AddIcon />}
          onClick={() => setOpen(true)}>
          {t('companies.new')}
        </Button>
      </Stack>

      {created && (
        <Alert severity="success" sx={{ mb: 1.5 }}
          onClose={() => setCreated(null)}>
          {t('companies.createdHint', { name: created.name_en })}
        </Alert>
      )}

      {q.isLoading ? <><Skeleton /><Skeleton /></> : q.isError ? (
        <Alert severity="error">{errorMessage(q.error)}</Alert>
      ) : (
        <Box className="ruled"
          sx={{ border: `1px solid ${c.border}`, borderRadius: 1 }}>
          {(q.data ?? []).map((co) => (
            <Stack key={co.id} direction="row" alignItems="center" spacing={1.5}
              sx={{ px: 1.5, py: 1.1 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13.5, fontWeight: 500 }} noWrap>
                  {co.name_en}
                </Typography>
                <Typography className="num" sx={{ fontSize: 11.5, color: c.inkMuted }}>
                  {co.code}
                  {co.year_start && co.year_end && (
                    <> · {t('companies.year')} {gregorian(co.year_start, i18n.language)}
                      {' – '}{gregorian(co.year_end, i18n.language)}</>
                  )}
                  {co.vat_number && <> · VAT {co.vat_number}</>}
                </Typography>
              </Box>
              {co.is_current && (
                <Chip size="small" label={t('companies.current')}
                  sx={{ height: 20, fontSize: 10, bgcolor: c.primarySoft }} />
              )}
              <Chip size="small" variant="outlined"
                label={t('companies.users', { count: co.user_count })}
                sx={{ height: 20, fontSize: 10 }} />
            </Stack>
          ))}
        </Box>
      )}

      {open && (
        <NewCompanyDialog onClose={() => setOpen(false)}
          onCreated={(co) => { setCreated(co); setOpen(false) }} />
      )}
    </>
  )
}


function NewCompanyDialog({ onClose, onCreated }: {
  onClose: () => void
  onCreated: (c: CompanyRow) => void
}) {
  const { t, i18n } = useTranslation()
  const qc = useQueryClient()
  const { refreshMe } = useAuth()
  const [form, setForm] = useState({
    name_en: '', name_ar: '', vat_number: '', cr_number: '',
    start_date: thisYearStart(),
  })
  const [error, setError] = useState<string | null>(null)
  const set = (k: keyof typeof form, v: string) =>
    setForm((f) => ({ ...f, [k]: v }))

  const end = useMemo(() => yearEnd(form.start_date), [form.start_date])

  const save = useMutation({
    mutationFn: () => createCompany({
      name_en: form.name_en.trim(),
      start_date: form.start_date,
      name_ar: form.name_ar.trim() || undefined,
      vat_number: form.vat_number.trim() || undefined,
      cr_number: form.cr_number.trim() || undefined,
    }),
    onSuccess: async (co) => {
      await qc.invalidateQueries({ queryKey: ['companies'] })
      // The switcher reads the company list from /me. Without this the
      // new company exists but cannot be reached until a reload -- which
      // looks exactly like the creation failed.
      await refreshMe()
      onCreated(co)
    },
    onError: (e) => setError(errorMessage(e)),
  })

  const ready = !!form.name_en.trim() && !!end

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>{t('companies.new')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}
          onClose={() => setError(null)}>{error}</Alert>}
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField label={t('companies.nameEn')} value={form.name_en} autoFocus
            onChange={(e) => set('name_en', e.target.value)} />
          <TextField label={t('companies.nameAr')} value={form.name_ar}
            inputProps={{ dir: 'rtl' }}
            onChange={(e) => set('name_ar', e.target.value)} />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label={t('companies.vat')} value={form.vat_number}
              sx={{ flex: 1 }} inputProps={{ inputMode: 'numeric' }}
              onChange={(e) => set('vat_number', e.target.value)} />
            <TextField label={t('companies.cr')} value={form.cr_number}
              sx={{ flex: 1 }} inputProps={{ inputMode: 'numeric' }}
              onChange={(e) => set('cr_number', e.target.value)} />
          </Stack>
          <TextField label={t('companies.yearStarts')} type="date"
            value={form.start_date} InputLabelProps={{ shrink: true }}
            onChange={(e) => set('start_date', e.target.value)}
            helperText={end
              ? t('companies.yearRuns', {
                  start: gregorian(form.start_date, i18n.language),
                  end: gregorian(end, i18n.language) })
              : ' '} />
          {/* Said before the button, not discovered after: the new company
              is opened only by its creator until someone is given it. */}
          <Typography variant="caption" sx={{ color: c.inkMuted }}>
            {t('companies.whatHappens')}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button color="inherit" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="contained" disabled={!ready || save.isPending}
          onClick={() => save.mutate()}>
          {save.isPending ? t('common.loading') : t('companies.create')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
